import { TelemetryEvent, TelemetrySink, DataField, LogLevel, Category, logNotification, logError, cloneEvent } from '@ms/oteljs';
import { ApplicationInsights, IExtendedConfiguration } from './ApplicationInsights';
import { Preprocessor } from './Preprocessor';
import { DataClassificationChecker } from './DataClassificationChecker';
import { getOneDSData, ARIA_OFFICE_TELEMETRY_TOKEN } from './GetOneDSData';
import { OneDSEndpoint } from './OneDSEndpoint';
import { PartAPlugin } from './PartAPlugin';
import { INotificationListener, IPlugin, IExtendedTelemetryItem, ITelemetryItem } from '@ms/1ds-core-js';
import { IXHROverride } from '@ms/1ds-post-js';
import { Utils } from './Utils';
import { PerfManager } from './PerfManager';

// import { IPayloadData } from '@ms/1ds-post-js/dist-esm/src/DataModels'; // 1DS SDK post package version 2.1.4 does not export this :/

// Indirect type dependencies of OneDSSinkProperties
// TODO: Unfortunately the 1DS SDK does not follow the conventions define by the api-extractor tool causing it to fail, this making it
// impossible to check this in w/o pushing changes through 1DS SDK first :/
// export { INotificationListener, ITelemetryItem, IXHROverride, IPayloadData };

/**
 * Properties for configuring the 1DS sink
 */
export interface OneDSSinkProperties {
  /**
   * The endpoint URL. Should generally be OneDSEndpoint.*, but can also be specified as a custom URL string.
   */
  endpointUrl: OneDSEndpoint | string;

  /**
   * Adds a notification listener. The SDK calls methods on the listener when an appropriate notification
   * (ex: event sent/dropped) is raised.
   */
  notificationListener?: INotificationListener;

  /**
   * The HTTP override that should be used to send requests.
   * CAUTION: The SDK does not provide an indication of whether the request is made during shutdown, and latest
   * version of the modern browsers no longer allow synchronous requests during shutdown (and asynchronous ones
   * will be simple ignored). In the idea case the SDK would provide that information which the caller can then
   * use to call special fetch w/ keepalive or sendbeacon API, but since it doesn't you can only safely use
   * this override for unittesting purposes.
   */
  httpXHROverride?: IXHROverride;

  /**
   * Override for setTimeout. OneDS SDK uses setTimeout for scheduling the upload task and event notification callbacks.
   */
  setTimeoutOverride?: typeof setTimeout;

  /**
   * Override for clearTimeout. OneDS SDK uses setTimeout for rescheduling the upload task.
   */
  clearTimeoutOverride?: typeof clearTimeout;

  /**
   * The frequency in milliseconds with which the upload task uploads events to the collector.
   */
  uploadFrequency?: number;

  /**
   * The number of events that can be kept in memory before the SDK starts to drop events. By default, this is 10,000.
   */
  eventsLimitInMem?: number;

  /**
   * Callback functions for SDK performance characteristics.
   */
  stats?: IStatsCallbacks;

  /**
   * Disables OTel's automatic tracking of SDKs processed/sent/dropped events.
   */
  disableStatsTracking?: boolean;

  /**
   * Plugins to be passed to the underlying SDK
   */
  plugins?: IPlugin[];

  /**
   * A callback function for events sent through this sink.
   * If this function is set, then this sink does not load its own instance of the 1DS SDK.
   * Instead, events are reported via this callback. Other parameters like stats and plugins
   * will not apply.
   */
  trackOverride?: typeof ApplicationInsights.prototype.track;
}

/**
 * An interface for callback functions for performance metrics
 */
export interface IStatsCallbacks {
  /**
   * Callback with how long the SDK takes to process (serialize + send) a batch of events into one request.
   */
  requestProcessingStats: TimeStatsCallback;

  /**
   * Callback with how many bytes the SDK puts out on the wire.
   */
  networkStats: NetworkStatsCallback;
}

export type TimeStatsCallback = (duration: number, numOfEvents: number) => void;
export type NetworkStatsCallback = (size: number) => void;

/**
 * OTelJS 1DS Sink sends telemetry using the Web 1DS sdk
 */
export class OneDSSink implements TelemetrySink {
  /**
   * Number of telemetry events processed
   * Also equal to the Event Sequence
   */
  public eventsProcessed: number = 0;

  /**
   * Number of telemetry events sent
   */
  public eventsSent: number = 0;

  /**
   * Number of telemetry events discarded by the 1DS SDK
   */
  public eventsDiscarded: number = 0;

  /**
   * Data fields that are sent on every event.
   * This variable stores the data fields that have been set on the PartAPlugin
   */
  private _persistentDataFields: DataField[] = [];
  private _partADataFieldsPlugin: PartAPlugin;
  private _preprocessors: Preprocessor[] = [];
  private _dataClassificationChecker: DataClassificationChecker;
  private _applicationInsights?: ApplicationInsights;
  private _oneDSSinkSessionId: string;
  private _epoch: string;
  private _trackOverride?: (item: IExtendedTelemetryItem | ITelemetryItem) => void;

  /**
   * Constructs an 1DS Sink
   * @param persistentDataFields - Data fields to be sent with every event; these data fields are assumed to
   * be top-level static fields, and will not be prepended with "Data."
   * @param endpointUrl - Endpoint to send 1DS events to
   */
  constructor(persistentDataFields: DataField[], oneDSSinkProperties: OneDSSinkProperties) {
    this._partADataFieldsPlugin = new PartAPlugin();

    if (oneDSSinkProperties.trackOverride) {
      this._trackOverride = oneDSSinkProperties.trackOverride;
    } else {
      if (!oneDSSinkProperties || !oneDSSinkProperties.endpointUrl) {
        throw new Error('Missing Endpoint Url');
      }
      const plugins: IPlugin[] = [this._partADataFieldsPlugin];
      if (oneDSSinkProperties.plugins) {
        plugins.push(...oneDSSinkProperties.plugins);
      }

      const appInsightsConfig: IExtendedConfiguration = {
        instrumentationKey: ARIA_OFFICE_TELEMETRY_TOKEN,
        endpointUrl: oneDSSinkProperties.endpointUrl,
        channelConfiguration: {
          eventsLimitInMem: oneDSSinkProperties.eventsLimitInMem,
          httpXHROverride: oneDSSinkProperties.httpXHROverride,
          setTimeoutOverride: oneDSSinkProperties.setTimeoutOverride,
          clearTimeoutOverride: oneDSSinkProperties.clearTimeoutOverride,
          ignoreMc1Ms0CookieProcessing: true
        },
        disableCookiesUsage: true
      };

      if (oneDSSinkProperties.stats && oneDSSinkProperties.stats.networkStats && appInsightsConfig.channelConfiguration) {
        appInsightsConfig.channelConfiguration.payloadListener = (originalPayload, modifiedPayload) => {
          const actualPayload = modifiedPayload ? modifiedPayload : originalPayload;
          if (actualPayload.data) {
            oneDSSinkProperties.stats?.networkStats(actualPayload.data.length);
          }
        };
      }

      this._applicationInsights = new ApplicationInsights();
      this._applicationInsights.initialize(appInsightsConfig, plugins);
      this._applicationInsights.setUploadFrequency(oneDSSinkProperties.uploadFrequency);
      this._applicationInsights.connectNotificationListener(oneDSSinkProperties.notificationListener);
      if (oneDSSinkProperties.stats) {
        this._applicationInsights.setPerfMgr(new PerfManager(oneDSSinkProperties.stats));
      }

      if (!oneDSSinkProperties.disableStatsTracking) {
        this.hookUp1DSNotifications(this._applicationInsights);
      }
    }

    this._oneDSSinkSessionId = Utils.newGuid();
    this._epoch = new Date().getTime().toString();

    this._dataClassificationChecker = new DataClassificationChecker();
    this.addPreprocessor(this._dataClassificationChecker);

    this.setPersistentDataFields(persistentDataFields);
  }

  /**
   * Preprocess and send telemetry using the 1DS Sink
   * @param telemetryEvent - The telemetry event to be sent
   * @param timestamp - Time the event was sent
   */
  public sendTelemetryEvent(telemetryEvent: TelemetryEvent, timestamp?: number) {
    let internalTelemetryEvent = cloneEvent(telemetryEvent);
    try {
      for (let i = 0; i < this._preprocessors.length; i++) {
        if (!this._preprocessors[i].processEvent(internalTelemetryEvent)) {
          return;
        }
      }
      this.sendEventInternal(internalTelemetryEvent, timestamp);
    } catch (error) {
      logError(Category.Sink, '1DS Sink', error);
    }
  }

  /**
   * Get the data fields that are set on every event
   */
  public getPersistentDataFields(): DataField[] {
    return this._persistentDataFields;
  }

  /**
   * Set data fields to be set on every event.
   * @param dataFields - Data fields to be set on every event; will not be prepended with "Data."
   */
  public setPersistentDataFields(dataFields: DataField[]) {
    this._partADataFieldsPlugin.addDataFields(dataFields);
    this._persistentDataFields = dataFields;
  }

  /**
   * Add a preprocessor, which can process events before they get sent
   * @param preprocessor - The preprocessor to add
   */
  public addPreprocessor(preprocessor: Preprocessor) {
    this._preprocessors.push(preprocessor);
  }

  /**
   * Enable sending full events to Aria. Generally the user must explicitly allow full events to be sent.
   * @param enabled - Whether full events are enabled
   */
  public setFullEventsEnabled(enabled: boolean) {
    this._dataClassificationChecker.setFullEventsEnabled(enabled);
  }

  /**
   * Prepares and sends an event to Aria using the 1DS SDK, bypassing OTel preprocessors
   * Generally, external callers should use sendTelemetryEvent() instead.
   * @param telemetryEvent - The telemetry event to be sent
   * @param timestamp - Time the event was sent
   */
  sendEventInternal(telemetryEvent: TelemetryEvent, timestamp?: number) {
    this.eventsProcessed++;

    if (!telemetryEvent.telemetryProperties || !telemetryEvent.telemetryProperties.ariaTenantToken) {
      throw new Error('Missing Tenant Token');
    }

    const data = getOneDSData({
      telemetryEvent,
      oneDSSinkSessionId: this._oneDSSinkSessionId,
      eventSequence: this.eventsProcessed,
      timestamp
    });

    const oneDsEvent = {
      iKey: telemetryEvent.telemetryProperties!.ariaTenantToken,
      name: telemetryEvent.eventName,
      data: data,
      time: new Date().toISOString(),
      baseType: 'EventData',
      ext: {
        sdk: {
          seq: this.eventsProcessed,
          epoch: this._epoch
        }
      }
    };
    if (this._trackOverride) {
      this._partADataFieldsPlugin?.processTelemetry(oneDsEvent);
      this._trackOverride(oneDsEvent);
    } else if (this._applicationInsights) {
      this._applicationInsights.track(oneDsEvent);
    }
  }

  private hookUp1DSNotifications(applicationInsights: ApplicationInsights) {
    applicationInsights.addNotificationListener({
      eventsSent: events => {
        logNotification(LogLevel.Info, Category.Transport, () => {
          return `Successfully sent ${events.length} event(s)`;
        });
        logNotification(LogLevel.Verbose, Category.Transport, () => {
          return `Sent event(s) details : ${JSON.stringify(events, null, 2)}`;
        });
        this.eventsSent += events.length;
      },
      eventsDiscarded: (events, reason: number) => {
        logNotification(LogLevel.Error, Category.Transport, () => {
          return `Discarded ${events.length} event(s) because ${reason}`;
        });
        logNotification(LogLevel.Verbose, Category.Transport, () => {
          return `Discarded event(s) details : ${JSON.stringify(events, null, 2)}`;
        });
        this.eventsDiscarded += events.length;
      }
    });
  }

  /**
   * Notifies the sink to flush the events.
   * @param async - Determines whether the operation is synchrounous or asynchrounous
   */
  public flush(async: boolean) {
    if (this._applicationInsights) {
      this._applicationInsights.flush(async);
    }
  }

  /**
   * Notifies the sink to send all outstanding events and shutdown.
   */
  public shutdown() {
    if (this._applicationInsights) {
      try {
        this._applicationInsights.shutdown();
      } catch (e) {
        logNotification(LogLevel.Error, Category.Transport, () => 'An error occurred on shutdown');
      }
    }
  }
}
